import { useMemo } from 'react'
import { useI18n } from '../i18n/useI18n'

export type TabId =
  | 'hideout'
  | 'survivors'
  | 'expeditions'
  | 'quests'
  | 'stash'

type Tab = {
  id: TabId
  labelKey:
    | 'nav_hub'
    | 'nav_people'
    | 'nav_trips'
    | 'nav_quests'
    | 'nav_stash'
  glyph: string
}

type Props = {
  active: TabId
  onChange: (tab: TabId) => void
  badges?: Partial<Record<TabId, number>>
  dots?: Partial<Record<TabId, boolean>>
}

const TAB_DEFS: Tab[] = [
  { id: 'hideout', labelKey: 'nav_hub', glyph: '🏚' },
  { id: 'survivors', labelKey: 'nav_people', glyph: '👥' },
  { id: 'expeditions', labelKey: 'nav_trips', glyph: '🧭' },
  { id: 'quests', labelKey: 'nav_quests', glyph: '📜' },
  { id: 'stash', labelKey: 'nav_stash', glyph: '📦' },
]

export function BottomNav({ active, onChange, badges, dots }: Props) {
  const { t } = useI18n()
  const tabs = useMemo(
    () =>
      TAB_DEFS.map((tab) => ({
        ...tab,
        label: t(tab.labelKey),
      })),
    [t],
  )

  return (
    <nav data-tutorial-id="bottom-nav" className="fixed inset-x-0 bottom-0 z-40 flex justify-center border-t border-stone-800 bg-stone-950/95 backdrop-blur">
      <div className="flex w-full max-w-[430px]">
        {tabs.map((tab) => {
          const isActive = tab.id === active
          const badge = badges?.[tab.id] ?? 0
          const hasDot = dots?.[tab.id] ?? false
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`relative flex flex-1 flex-col items-center gap-0.5 py-3 text-[10px] uppercase tracking-wider transition-colors ${
                isActive
                  ? 'text-amber-300'
                  : 'text-stone-500 hover:text-stone-300'
              }`}
            >
              <span className="text-xl leading-none">{tab.glyph}</span>
              <span>{tab.label}</span>
              {badge > 0 && (
                <span className="absolute right-[20%] top-1.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-amber-600 px-1 font-mono text-[10px] text-stone-950">
                  {badge}
                </span>
              )}
              {hasDot && badge === 0 && (
                <span className="absolute right-[22%] top-2 h-2 w-2 rounded-full bg-amber-500" />
              )}
              {isActive && (
                <span className="absolute inset-x-4 top-0 h-0.5 bg-amber-500" />
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
